import { CALCULATE_CART, CREATE_DRAFT_ORDER } from '../../constants/endpoints';
import { getCurrencyExponent } from '../../lib/currencyFormatters';
import { setLoading } from '../page';
import { RECEIVE_IS_LOADING_REFUND_ESTIMATION } from '../refunds';
import { toggleTooltip } from '../tooltip';

export const STEPS = {
  RETURN_ITEM_DETAILS: 'RETURN_ITEM_DETAILS',
  EXCHANGE_ITEM_PICKER: 'EXCHANGE_ITEM_PICKER',
};

export const RECEIVE_UNEVEN_EXCHANGE_DATA = 'RECEIVE_UNEVEN_EXCHANGE_DATA';
export const SET_UNEVEN_EXCHANGE_STEP = 'SET_UNEVEN_EXCHANGE_STEP';
export const SET_EXCHANGE_TO = 'SET_EXCHANGE_TO';
export const RESET_UNEVEN_EXCHANGES_STATE = 'RESET_UNEVEN_EXCHANGES_STATE';
export const RECEIVE_CALCULATE_CART_DATA = 'RECEIVE_CALCULATE_CART_DATA';
export const RECEIVE_CREATE_CART_DATA = 'RECEIVE_CREATE_CART_DATA';
export const RECEIVE_UNEVEN_EXCHANGE_PAYMENT_AUTH = 'RECEIVE_UNEVEN_EXCHANGE_PAYMENT_AUTH';
export const SET_IS_UNEVEN_EXCHANGE_SUBMIT = 'SET_IS_UNEVEN_EXCHANGE_SUBMIT';

const mapInventoryDataToHeadlessFormat = (populatedInventory) => {
  const { inventory, options } = populatedInventory;
  const productListIds = [...new Set(inventory.map((item) => item.productId))];
  const exchangeOptions = [
    {
      id: 'product',
      name: 'Product',
      displayType: 'image',
      position: 0,
      values: productListIds.map((id, index) => {
        const item = inventory.find(({ productId }) => id === productId);
        return {
          name: item.title,
          value: id,
          imageUrl: item.item_image,
          position: index,
        };
      }),
    },
    ...options.map((option, optionIndex) => ({
      id: option.code,
      name: option.title,
      position: optionIndex + 1,
      values: option.values.map((value, valueIndex) => ({
        value,
        name: value,
        position: valueIndex,
      })),
    })),
  ];
  const exchangeVariants = inventory.map((item) => {
    const variantInfo = {
      product: {
        optionId: 'product',
        optionName: 'Product',
        optionPosition: 0,
        value: item.productId,
        name: item.title,
        position: productListIds.indexOf(item.productId),
      },
    };
    options.forEach((option, index) => {
      const optionValue = item.attributes[option.code];
      if (optionValue) {
        variantInfo[option.code] = {
          optionId: option.code,
          optionName: option.title,
          optionPosition: index + 1,
          value: optionValue,
          name: optionValue,
          position: option.values.indexOf(optionValue),
        };
      }
    });
    return {
      productId: item.productId,
      variantId: item.ext_id,
      name: item.title,
      price: {
        value: parseInt(item.price, 10),
        currency: 'USD',
      },
      sku: item.sku,
      size: item.attributes?.size,
      imageUrl: item.item_image,
      variantInfo,
      available: item.exchange_eligibility,
      quantity: item.available_quantity,
    };
  });

  const maxAvailableQty = exchangeVariants.reduce((highestQty, { available, quantity }) => {
    if (available && quantity > highestQty) {
      return quantity;
    }
    return highestQty;
  }, 0);

  const quantityOptions = {};
  // eslint-disable-next-line no-plusplus
  for (let i = 1; i <= maxAvailableQty; i++) {
    quantityOptions[i] = true;
  }

  return { exchangeOptions, exchangeVariants, quantityOptions };
};

export const receiveUnevenExchangeData = ({ inventory, options }) => {
  const { exchangeOptions, exchangeVariants, quantityOptions } = mapInventoryDataToHeadlessFormat({
    inventory,
    options,
  });
  return {
    type: RECEIVE_UNEVEN_EXCHANGE_DATA,
    data: {
      exchangeOptions,
      exchangeVariants,
      quantityOptions,
    },
  };
};

export const setUnevenExchangeStep = (step) => ({
  type: SET_UNEVEN_EXCHANGE_STEP,
  data: {
    step,
  },
});

export const setExchangeTo = (exchangeTo) => ({
  type: SET_EXCHANGE_TO,
  data: {
    exchangeTo,
  },
});

export const resetUnevenExchangesState = () => ({
  type: RESET_UNEVEN_EXCHANGES_STATE,
});

export const calculateCart = (order) => async (dispatch, getState) => {
  if (order.return_items?.every((item) => !item.exchange_item)) return;
  dispatch({
    type: SET_IS_UNEVEN_EXCHANGE_SUBMIT,
    data: {
      isUnevenExchangeSubmit: true,
    },
  });
  const {
    retailerSettings: { name: retailerName } = {},
    returnOptions: { selectedOptionId } = {},
    orderInfo: { currentOrderNumber } = {},
    tooltip: { returnPostErrorTooltip } = {},
  } = getState();
  try {
    dispatch({
      type: RECEIVE_IS_LOADING_REFUND_ESTIMATION,
      data: {
        isLoading: true,
      },
    });
    const response = await fetch(CALCULATE_CART({ retailerName }), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        return_items: order.return_items
          .filter((item) => !item.exchange_item)
          .map((item) => ({
            line_item_id: item.product_id,
            variant_id: item.product_sku,
            return_reason: item.return_desc,
            return_reason_id: item.return_reason_code_id,
            comment: item.user_comment || 'placeholder',
            quantity: item.return_qty,
            image_srcs: [],
            price: {
              price_in_cents: parseInt(
                (item.item_price * getCurrencyExponent('USD')).toFixed(),
                10,
              ),
              currency: 'USD',
            },
          })),
        exchange_items: order.return_items
          .filter((item) => item.exchange_item)
          .map((item) => ({
            product_id: item.product_id,
            variant_id: item.product_sku,
            display_name: item.exchange_item.title,
            image_src: item.exchange_item.item_image,
            sku: item.product_sku,
            new_sku: item.exchange_item.sku,
            new_product_id: item.exchange_item.product_id,
            new_variant_id: item.exchange_item.variant_id,
            options: [
              {
                product: item.exchange_item.title,
                ...item.exchange_item.attributes,
              },
            ],
            price: {
              price_in_cents: parseInt(
                (item.exchange_item.price * getCurrencyExponent('USD')).toFixed(),
                10,
              ),
              currency: item.exchange_item.currency || 'USD',
            },
            quantity: item.return_qty,
          })),
        order_information: {
          order_number: currentOrderNumber,
          ext_order_id: currentOrderNumber,
          order_date: new Date().toISOString().slice(0, 10),
          from_address: {},
        },
        return_method: selectedOptionId,
        refund_method: 'original_form_of_payment',
      }),
    });
    const json = await response.json();
    if (json.status === 'FAILURE') {
      throw new Error('Calculate Cart Error');
    }
    if (returnPostErrorTooltip) {
      dispatch(toggleTooltip('returnPostErrorTooltip'));
    }
    await dispatch({
      type: RECEIVE_CALCULATE_CART_DATA,
      data: {
        calculatedCart: json,
      },
    });
  } catch (err) {
    if (!returnPostErrorTooltip) {
      dispatch(toggleTooltip('returnPostErrorTooltip'));
    }
    console.log(err);
  } finally {
    dispatch({
      type: RECEIVE_IS_LOADING_REFUND_ESTIMATION,
      data: {
        isLoading: false,
      },
    });
  }
};

export const createDraftOrder = () => async (dispatch, getState) => {
  const {
    retailerSettings: { name: retailerName } = {},
    orderInfo: { currentOrderNumber },
    unevenExchanges: { calculatedCart } = {},
    tooltip: { returnPostErrorTooltip } = {},
  } = getState();
  try {
    const response = await fetch(CREATE_DRAFT_ORDER({ retailerName }), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        cart: calculatedCart.cart,
        order_information: {
          order_number: currentOrderNumber,
        },
      }),
    });
    const json = await response.json();
    if (!json.order_id) throw new Error('order_id is missing');
    await dispatch({
      type: RECEIVE_CREATE_CART_DATA,
      data: {
        createdCart: json,
      },
    });
  } catch (err) {
    if (!returnPostErrorTooltip) {
      dispatch(toggleTooltip('returnPostErrorTooltip'));
    }
    console.log(err);
    dispatch(setLoading(false));
  }
};

export const receiveUnevenExchangePaymentAuth = (paymentAuthorization) => async (dispatch) => {
  await dispatch({
    type: RECEIVE_UNEVEN_EXCHANGE_PAYMENT_AUTH,
    data: {
      paymentAuthorization,
    },
  });
};
